// The phone's top app bar for screens pushed over the tabs (Edit details, Add a child, an address,
// Cart...): an up arrow and the screen's title, on the page ground with no rule, like the school
// header it replaces. The arrow does what Android Back does; a screen that must ask first passes
// its own `onBack`.
import { useRouter } from "expo-router";
import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { ChevronIcon } from "@/ui/icons/ChevronIcon";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

/** Material's small top app bar height. */
export const APP_BAR_HEIGHT = 64;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    bar: {
      height: APP_BAR_HEIGHT,
      flexDirection: "row",
      alignItems: "center",
      gap: t.space.xxs,
      paddingHorizontal: t.space.xxs,
      backgroundColor: t.color.background,
    },
    up: {
      width: t.size.touchTarget + t.space.xxs,
      height: t.size.touchTarget + t.space.xxs,
      borderRadius: t.radius.button,
    },
    title: { flex: 1, textTransform: "lowercase" },
    end: { flexDirection: "row", alignItems: "center" },
  }),
);

export type AppBarProps = {
  title: string;
  /** Replaces the default (back, or `fallbackHref` when there is nothing to go back to). */
  onBack?: () => void;
  /** Where the arrow goes when the screen was opened directly (a link, a restart). */
  fallbackHref?: "/" | "/profile";
  /** Trailing actions (icon buttons). */
  end?: ReactNode;
};

export function AppBar({
  title,
  onBack,
  fallbackHref = "/",
  end,
}: AppBarProps) {
  const styles = useStyles();
  const theme = useTheme();
  const router = useRouter();
  const back =
    onBack ??
    (() => (router.canGoBack() ? router.back() : router.replace(fallbackHref)));
  return (
    <View style={styles.bar} testID="app-bar">
      <PressableTarget
        accessibilityLabel="Back"
        onPress={back}
        style={styles.up}
        android_ripple={{ color: theme.color.border, borderless: true }}
        testID="app-bar-back"
      >
        <ChevronIcon
          direction="left"
          color={theme.color.text}
          size={theme.size.icon.lg}
        />
      </PressableTarget>
      <Text
        variant="heading"
        weight="light"
        size="x3l"
        numberOfLines={1}
        style={styles.title}
        accessibilityRole="header"
        aria-level={1}
      >
        {title}
      </Text>
      {end ? <View style={styles.end}>{end}</View> : null}
    </View>
  );
}
